/**
 * The chunk queue — store-and-forward persistence for the background uploader (FSD §4.5).
 *
 * Every recorded chunk is written here with its `(shotId, seq, sha256, chainHash, headers)` BEFORE
 * the uploader sees it; every shot's seal (chunkCount, finalChainHash, signature) is written when
 * recording stops. Two storages behind one interface:
 *   • `IndexedDbQueueStorage` — the real thing: survives a tab reload / app switch / Wi-Fi-later.
 *   • `MemoryQueueStorage`    — the honest fallback where IndexedDB is unavailable (private mode on
 *                               some browsers). `openQueueStorage()` reports which one is in force
 *                               so telemetry and the receipt can say "resume needs this tab open".
 *
 * Chunk bytes are stored as ArrayBuffer (structured-clone safe everywhere). A shot's chunks carry
 * the shot's `attempt` number; a re-record bumps the attempt and deletes the old attempt's rows.
 */

import type { ShotSignature } from '@/api/capture';

export type ChunkStatus = 'pending' | 'done' | 'refused';
export type ShotUploadStatus = 'recording' | 'sealed' | 'complete' | 'refused' | 'abandoned';

export interface StoredChunkHeaders {
  readonly capturedAt: string;
  readonly promptId: string;
  readonly geo: string | null;
  readonly imu: string | null;
}

export interface StoredChunk {
  readonly key: string;
  readonly visitId: string;
  readonly shotId: string;
  readonly ordinal: number;
  readonly attempt: number;
  readonly seq: number;
  readonly bytes: ArrayBuffer;
  readonly sha256: string;
  readonly chainHash: string;
  readonly headers: StoredChunkHeaders;
  readonly status: ChunkStatus;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly enqueuedAt: number;
}

export interface StoredShot {
  readonly key: string;
  readonly visitId: string;
  readonly shotId: string;
  readonly ordinal: number;
  readonly attempt: number;
  readonly promptKey: string;
  readonly kind: 'STILL' | 'CLIP';
  readonly mimeType: string;
  readonly chunkCount: number | null;
  readonly finalChainHash: string | null;
  readonly durationS: number | null;
  readonly signature: ShotSignature | null;
  readonly status: ShotUploadStatus;
  readonly refusalCode: string | null;
  readonly refusalMessage: string | null;
  readonly serverSha256: string | null;
  readonly completeAttempts: number;
  readonly updatedAt: number;
}

export const chunkKey = (visitId: string, shotId: string, attempt: number, seq: number): string =>
  `${visitId}|${shotId}|${attempt}|${seq}`;
export const shotKey = (visitId: string, shotId: string): string => `${visitId}|${shotId}`;

export interface QueueStorage {
  putChunk(c: StoredChunk): Promise<void>;
  updateChunk(key: string, patch: Partial<StoredChunk>): Promise<void>;
  putShot(s: StoredShot): Promise<void>;
  updateShot(key: string, patch: Partial<StoredShot>): Promise<void>;
  getShot(key: string): Promise<StoredShot | null>;
  /** Ordered by ordinal. */
  listShots(visitId: string): Promise<StoredShot[]>;
  /** Ordered by (ordinal, seq); optionally narrowed to one shot's current attempt. */
  listChunks(visitId: string, shotId?: string, attempt?: number): Promise<StoredChunk[]>;
  deleteShotAttempt(visitId: string, shotId: string, attempt: number): Promise<void>;
  clearVisit(visitId: string): Promise<void>;
  /**
   * Every visitId this storage still holds a row for — the input to DEC-097 T2's boot sweep.
   * Reads KEYS only: it must never pull a stale visit's chunk bytes into memory to decide whether
   * to delete them (a phone with ten un-purged clips would pay hundreds of MB for the question).
   */
  listVisitIds(): Promise<string[]>;
}

const byOrdinalSeq = (a: StoredChunk, b: StoredChunk): number => a.ordinal - b.ordinal || a.seq - b.seq;
const byOrdinal = (a: StoredShot, b: StoredShot): number => a.ordinal - b.ordinal;

// ── memory ─────────────────────────────────────────────────────────────────────────────────────
export class MemoryQueueStorage implements QueueStorage {
  private chunks = new Map<string, StoredChunk>();
  private shots = new Map<string, StoredShot>();

  async putChunk(c: StoredChunk): Promise<void> {
    this.chunks.set(c.key, c);
  }
  async updateChunk(key: string, patch: Partial<StoredChunk>): Promise<void> {
    const cur = this.chunks.get(key);
    if (cur) this.chunks.set(key, { ...cur, ...patch });
  }
  async putShot(s: StoredShot): Promise<void> {
    this.shots.set(s.key, s);
  }
  async updateShot(key: string, patch: Partial<StoredShot>): Promise<void> {
    const cur = this.shots.get(key);
    if (cur) this.shots.set(key, { ...cur, ...patch, updatedAt: Date.now() });
  }
  async getShot(key: string): Promise<StoredShot | null> {
    return this.shots.get(key) ?? null;
  }
  async listShots(visitId: string): Promise<StoredShot[]> {
    return [...this.shots.values()].filter((s) => s.visitId === visitId).sort(byOrdinal);
  }
  async listChunks(visitId: string, shotId?: string, attempt?: number): Promise<StoredChunk[]> {
    return [...this.chunks.values()]
      .filter(
        (c) =>
          c.visitId === visitId &&
          (shotId === undefined || c.shotId === shotId) &&
          (attempt === undefined || c.attempt === attempt),
      )
      .sort(byOrdinalSeq);
  }
  async deleteShotAttempt(visitId: string, shotId: string, attempt: number): Promise<void> {
    for (const [k, c] of this.chunks) {
      if (c.visitId === visitId && c.shotId === shotId && c.attempt === attempt) this.chunks.delete(k);
    }
  }
  async clearVisit(visitId: string): Promise<void> {
    for (const [k, c] of this.chunks) if (c.visitId === visitId) this.chunks.delete(k);
    for (const [k, s] of this.shots) if (s.visitId === visitId) this.shots.delete(k);
  }
  async listVisitIds(): Promise<string[]> {
    const ids = new Set<string>();
    for (const c of this.chunks.values()) ids.add(c.visitId);
    for (const s of this.shots.values()) ids.add(s.visitId);
    return [...ids];
  }
}

// ── IndexedDB ───────────────────────────────────────────────────────────────────────────────────
export const IDB_NAME = 'vcip-svapp-upload';
export const IDB_VERSION = 1;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

export function openIndexedDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(IDB_NAME, IDB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains('chunks')) {
        db.createObjectStore('chunks', { keyPath: 'key' }).createIndex('visit', 'visitId');
      }
      if (!db.objectStoreNames.contains('shots')) {
        db.createObjectStore('shots', { keyPath: 'key' }).createIndex('visit', 'visitId');
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('IndexedDB open failed'));
    open.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

export class IndexedDbQueueStorage implements QueueStorage {
  constructor(private readonly db: IDBDatabase) {}

  static async open(factory: IDBFactory = indexedDB): Promise<IndexedDbQueueStorage> {
    return new IndexedDbQueueStorage(await openIndexedDb(factory));
  }

  private async write<T>(store: 'chunks' | 'shots', fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<void> {
    const tx = this.db.transaction(store, 'readwrite');
    fn(tx.objectStore(store));
    await txDone(tx);
  }

  private async byVisit<T>(store: 'chunks' | 'shots', visitId: string): Promise<T[]> {
    const tx = this.db.transaction(store, 'readonly');
    const rows = await req(tx.objectStore(store).index('visit').getAll(visitId) as IDBRequest<T[]>);
    await txDone(tx);
    return rows;
  }

  async putChunk(c: StoredChunk): Promise<void> {
    await this.write('chunks', (s) => s.put(c));
  }
  async updateChunk(key: string, patch: Partial<StoredChunk>): Promise<void> {
    const tx = this.db.transaction('chunks', 'readwrite');
    const store = tx.objectStore('chunks');
    const cur = (await req(store.get(key))) as StoredChunk | undefined;
    if (cur) store.put({ ...cur, ...patch });
    await txDone(tx);
  }
  async putShot(s: StoredShot): Promise<void> {
    await this.write('shots', (st) => st.put(s));
  }
  async updateShot(key: string, patch: Partial<StoredShot>): Promise<void> {
    const tx = this.db.transaction('shots', 'readwrite');
    const store = tx.objectStore('shots');
    const cur = (await req(store.get(key))) as StoredShot | undefined;
    if (cur) store.put({ ...cur, ...patch, updatedAt: Date.now() });
    await txDone(tx);
  }
  async getShot(key: string): Promise<StoredShot | null> {
    const tx = this.db.transaction('shots', 'readonly');
    const row = (await req(tx.objectStore('shots').get(key))) as StoredShot | undefined;
    await txDone(tx);
    return row ?? null;
  }
  async listShots(visitId: string): Promise<StoredShot[]> {
    return (await this.byVisit<StoredShot>('shots', visitId)).sort(byOrdinal);
  }
  async listChunks(visitId: string, shotId?: string, attempt?: number): Promise<StoredChunk[]> {
    return (await this.byVisit<StoredChunk>('chunks', visitId))
      .filter((c) => (shotId === undefined || c.shotId === shotId) && (attempt === undefined || c.attempt === attempt))
      .sort(byOrdinalSeq);
  }
  async deleteShotAttempt(visitId: string, shotId: string, attempt: number): Promise<void> {
    const rows = await this.listChunks(visitId, shotId, attempt);
    if (rows.length === 0) return;
    await this.write('chunks', (s) => {
      for (const r of rows) s.delete(r.key);
    });
  }
  /**
   * Delete one visit's rows in a single transaction, walking a KEY cursor: the rows are addressed
   * by key, so a purge of ten clips never decodes a byte of them into memory (the getAll-then-delete
   * shape this replaced did — on the phone that is the difference between a purge and an OOM).
   */
  private async deleteByVisit(store: 'chunks' | 'shots', visitId: string): Promise<void> {
    const tx = this.db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    const cursor = os.index('visit').openKeyCursor(IDBKeyRange.only(visitId));
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return;
      os.delete(c.primaryKey);
      c.continue();
    };
    await txDone(tx);
  }
  async clearVisit(visitId: string): Promise<void> {
    await this.deleteByVisit('chunks', visitId);
    await this.deleteByVisit('shots', visitId);
  }
  /** Distinct index keys via a KEY cursor — no record (and so no chunk byte) is ever materialised. */
  private async visitKeys(store: 'chunks' | 'shots'): Promise<string[]> {
    const tx = this.db.transaction(store, 'readonly');
    const cursor = tx.objectStore(store).index('visit').openKeyCursor(null, 'nextunique');
    const out: string[] = [];
    await new Promise<void>((resolve, reject) => {
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) {
          resolve();
          return;
        }
        out.push(String(c.key));
        c.continue();
      };
      cursor.onerror = () => reject(cursor.error ?? new Error('IndexedDB cursor failed'));
    });
    await txDone(tx);
    return out;
  }
  async listVisitIds(): Promise<string[]> {
    return [...new Set([...(await this.visitKeys('chunks')), ...(await this.visitKeys('shots'))])];
  }
}

// ── DEC-097 T2 — the phone does not keep the masters ───────────────────────────────────────────
/**
 * Purge one visit's buffered bytes off this device.
 *
 * WHEN (and only when): the server has the visit and has said so — the `POST /submit` receipt.
 * Before that the rows ARE the resume: a purge that ran early would silently destroy recordings the
 * representative was told are "safe on this phone".
 *
 * NEVER THROWS, AND NEVER HANGS. The purge is a storage-limitation control, not a step of the
 * submission: a browser that refuses the delete (private mode, quota, a closed connection) must not
 * take the receipt down with it, and a wedged IndexedDB transaction must not strand a representative
 * on the review screen holding a receipt the server has already issued. Past `PURGE_WAIT_MS` the
 * caller is released and the delete keeps running unattended; the boot sweep is the net that catches
 * whatever it failed to remove. The boolean is for a caller that wants to report the outcome
 * honestly; nobody may branch the flow on it.
 */
export const PURGE_WAIT_MS = 4_000;

export async function purgeVisit(storage: QueueStorage, visitId: string, waitMs: number = PURGE_WAIT_MS): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const purge = storage.clearVisit(visitId).then(
      () => true,
      () => false,
    );
    const released = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), waitMs);
    });
    return await Promise.race([purge, released]);
  } catch {
    return false;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * DEC-097 T2's boot sweep: on app boot no OTHER visit's frames may still be sitting on this phone.
 *
 * ONE DELIBERATE NARROWING, flagged for VCIP-REG rather than taken silently. The ruling of record
 * (DECISIONS_LOG DEC-097 T2) says "a boot sweep of stale visits"; the glance's mechanism column
 * says "sweep both stores for any visitId other than the current one". Taken literally, the second
 * wording also deletes a visit whose bytes have NOT reached the server — the offline-roof case the
 * whole store-and-forward queue exists for, and which the app promises out loud
 * ("Your recordings are safe on this phone — try again", `sv.review.submitFail`). So a visit is
 * swept unless it still holds a shot the uploader is holding for the server (`status === 'sealed'`),
 * which is precisely "the server does not have these bytes yet and the queue can still send them".
 *   • `recording` is NOT recoverable: the hash chain lives in memory, and `beginShot` deletes the
 *     previous attempt's rows on resume — those bytes can never be sealed, so they are swept.
 *   • `complete` / `refused` / `abandoned` are swept: the server has them, has rejected them, or
 *     they were dropped. Nothing here is a second copy of anything worth keeping.
 * Never throws; a visit that cannot be read is left alone and reported as retained.
 */
export async function sweepStaleVisits(
  storage: QueueStorage,
  keepVisitId: string | null,
): Promise<{ cleared: string[]; retained: string[] }> {
  const out: { cleared: string[]; retained: string[] } = { cleared: [], retained: [] };
  let ids: string[];
  try {
    ids = await storage.listVisitIds();
  } catch {
    return out;
  }
  for (const id of ids) {
    if (id === keepVisitId) continue;
    try {
      const shots = await storage.listShots(id);
      if (shots.some((s) => s.status === 'sealed')) {
        out.retained.push(id);
        continue;
      }
      await storage.clearVisit(id);
      out.cleared.push(id);
    } catch {
      out.retained.push(id);
    }
  }
  return out;
}

export type QueuePersistence = 'indexeddb' | 'memory';

/** IndexedDB when the browser offers it and it opens; memory otherwise — reported, never hidden. */
export async function openQueueStorage(
  /** `null` = no IndexedDB on this device (the honest memory fallback); omitted = the browser's. */
  factory: IDBFactory | null | undefined = typeof indexedDB !== 'undefined' ? indexedDB : null,
): Promise<{ storage: QueueStorage; persistence: QueuePersistence }> {
  if (factory) {
    try {
      return { storage: await IndexedDbQueueStorage.open(factory), persistence: 'indexeddb' };
    } catch {
      /* private mode / quota / blocked — fall back honestly */
    }
  }
  return { storage: new MemoryQueueStorage(), persistence: 'memory' };
}
